import { Component, OnDestroy, OnInit, HostListener, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { ToastrService } from 'ngx-toastr';
import { handle } from 'src/shared/functions/api.function';
import { EncodingDataService } from 'src/shared/services/encoding-data.service';
import { SettingService } from 'src/shared/services/setting.service';
import { CurrentUser } from '../../../../interfaces/current-user.interface';
import { Subject } from 'rxjs';
import { SharedService } from '@shared/services/shared.service';
const Swal = require('sweetalert2');

@Component({
    selector: 'app-my-account',
    templateUrl: './my-account.component.html',
    styleUrls: ['./my-account.component.scss'],
})
export class MyAccountComponent implements OnInit, OnDestroy {
    public userName: string = '';
    public currentUser!: CurrentUser;
    public newPasswordValue!: string;
    public confirmPasswordValue!: string;
    public passwordForm!: FormGroup;
    public submitted: boolean = false;
    public accountForm!: FormGroup;
    public authenticationTwoFactors = false;
    public isDropdownOpen: boolean = false;

    private destroy$ = new Subject<void>();
    public readonly loadingDoubleAuthentication$ =
        this.sharedService.loadingDoubleAuthentication$;
    public readonly loadingUserAuth$ = this.sharedService.loadingUserAuth$;
    constructor(
        private settingService: SettingService,
        private sharedService: SharedService,
        private router: Router,
        private fb: FormBuilder,
        private modalService: NgbModal,
        private toastrService: ToastrService,
        private loadingBarService: LoadingBarService,
        private encodingService: EncodingDataService,
        private elementRef: ElementRef
    ) {}

    ngOnInit() {
        this.sharedService.fetchUserAuth();
        this.sharedService.userAuth$.subscribe((user: CurrentUser) => {
            if (user) {
                this.currentUser = user;
                this.authenticationTwoFactors = user?.enable_2fa;
            }
        });
        this.initFormPassword();
        this.initFormAccount();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isDropdownOpen = false;
        }
    }

    toggleDropdown(event: Event): void {
        event.stopPropagation();
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    // Password
    public initFormPassword(): void {
        this.passwordForm = this.fb.group({
            old_password: [null, [Validators.required]],
            // new_password: [null, [Validators.required, CustomValidators.password]],
            // new_password_confirmation: [null, [Validators.required, CustomValidators.password]],
            new_password: [null, Validators.required],
            new_password_confirmation: [null, Validators.required],
        });
    }
    public openFormPassword(content) {
        this.isDropdownOpen = false;
        this.modalService.open(content, {
            backdrop: 'static',
            keyboard: false
        });
        this.passwordForm.reset();
    }
    public hideForm() {
        this.passwordForm.reset();
        this.modalService.dismissAll();
    }
    public openFormAuthenticationTwoFactors(content) {
        this.isDropdownOpen = false;
        this.modalService.open(content);
    }
    OnChangeNewPasswordvalue() {
        return this.passwordForm
            .get('nouveau_password')
            .valueChanges.subscribe((value) => {
                return (this.newPasswordValue = value);
            });
    }
    OnChangeConfirmPasswordvalue() {
        return this.passwordForm
            .get('confirm_password')
            .valueChanges.subscribe((value) => {
                return (this.confirmPasswordValue = value);
            });
    }

    // public handleUpdatePassword(): void {
    //   this.settingService
    //     .HandleUpdatePassword(this.passwordForm.value)
    //     .subscribe({
    //       next: (response) => {
    //         this.hideForm()
    //         this.router.navigateByUrl('auth/login')
    //           .then(() => window.location.reload());
    //         this.toastrService.success(response?.message);
    //       },
    //       error: (error) => {
    //         this.toastrService.error(error.error.message);
    //       }
    //     })
    // }

    async handleUpdatePassword(
        dataToSend: Object = this.passwordForm.value
    ): Promise<void> {
        this.submitted = true;
        if (
            this.passwordForm.get('new_password').value !==
            this.passwordForm.get('new_password_confirmation').value
        ) {
            this.passwordForm
                .get('new_password')
                ?.setErrors({ invalidPassword: true });
            this.passwordForm
                .get('new_password_confirmation')
                ?.setErrors({ invalidPassword: true });
        }
        if (this.passwordForm.valid) {
            const response: any = await handle(
                () => this.settingService.HandleUpdatePassword(dataToSend),
                this.toastrService,
                this.loadingBarService
            );
            if (response?.error === false) {
                this.handleSuccessful(response);
            }
        }
    }

    private handleSuccessful(response): void {
        this.toastrService.success(response.message);
        this.hideForm();
    }

    public onToggleAuthenticationTwoFactors() {
        const payload = { enable_2fa: this.authenticationTwoFactors };
        this.sharedService.fetchDoubleAuthentication(payload);
    }

    // Account
    public initFormAccount(): void {
        this.accountForm = this.fb.group({
            full_name: ['', [Validators.required]],
            email: ['', [Validators.required]],
            phone_number: ['', [Validators.required]],
            address: ['', [Validators.required]],
        });
    }
    public openFormAccount(account, data) {
        this.isDropdownOpen = false;
        // Concaténer last_name et first_name pour le champ full_name
        const fullName = `${data.last_name || ''} ${data.first_name || ''}`.trim();
        this.accountForm.get('full_name').patchValue(fullName);
        this.accountForm.get('email').patchValue(data.email);
        // Formater le numéro de téléphone avec le masque XX-XX-XX-XX-XX
        const phoneValue = data.phone_number || data.contacts || '';
        const formattedPhone = this.formatPhoneNumber(phoneValue);
        this.accountForm.get('phone_number').patchValue(formattedPhone);
        // Utiliser address ou adresse selon ce qui est disponible
        const addressValue = data.address || data.adresse || '';
        this.accountForm.get('address').patchValue(addressValue);
        // Désactiver les champs full_name et email
        this.accountForm.get('full_name').disable();
        this.accountForm.get('email').disable();
        this.modalService.open(account, {
            backdrop: 'static',
            keyboard: false
        });
    }

    private formatPhoneNumber(phone: string): string {
        if (!phone) return '';
        // Retirer tous les caractères non numériques
        const cleanPhone = phone.replace(/\D/g, '');
        // Appliquer le format XX-XX-XX-XX-XX
        if (cleanPhone.length === 10) {
            return cleanPhone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1-$2-$3-$4-$5');
        }
        // Si le numéro n'a pas exactement 10 chiffres, retourner tel quel
        return phone;
    }
    public hideFormAccount() {
        this.modalService.dismissAll();
        this.accountForm.reset();
    }

    public handleUpdateAdmin(): void {
        const userId = this.currentUser?.id;
        // Récupérer le numéro de téléphone et retirer les tirets
        const phoneNumber = this.accountForm.get('phone_number').value;
        const cleanPhoneNumber = phoneNumber ? phoneNumber.replace(/\D/g, '') : '';
        
        // Envoyer uniquement phone_number et address
        const formData = {
            phone_number: cleanPhoneNumber,
            address: this.accountForm.get('address').value
        };
        
        this.settingService.OnUpdateUserProfile(formData, userId).subscribe({
            next: (response) => {
                this.hideForm();
                this.router
                    .navigateByUrl('auth/login')
                    .then(() => window.location.reload());
                this.toastrService.success(response?.message);
            },
            error: (error) => {
                this.toastrService.error(error.error.message);
            },
        });
    }
    public handleLogout(): void {
        this.settingService.Logout({}).subscribe({
            next: (response) => {
                // this.currentUserService.removeCurrentUser();
                this.encodingService.removeData('token_data');
                this.encodingService.removeData('menu');
                this.encodingService.removeData('dashboard_links');
                this.encodingService.removeData('user_data');
                this.encodingService.removeData('token_data');
                this.encodingService.removeData('modules');
                // this.cryptoToken.clear();
                if (this.encodingService.getData('isProfil') || null) {
                    this.encodingService.removeData('isProfil');
                    window.location.reload();
                    // .then(() => window.location.reload());
                }
                this.router.navigateByUrl('auth/login');
                this.toastrService.success(response?.message);
            },
            error: (error) => {
                this.toastrService.error(error.error.message);
            },
        });
    }
    public logout(): void {
        this.isDropdownOpen = false;
        Swal.fire({
            title: 'En êtes vous sûr ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#569C5B',
            cancelButtonColor: '#dc3545',
            cancelButtonText: 'Annuler',
            confirmButtonText: 'Oui',
        }).then((result) => {
            if (result.isConfirmed) {
                this.handleLogout();
            }
        });
    }
}
